import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Target, 
  BarChart3, 
  Search, 
  Lightbulb, 
  Shield, 
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  FileText
} from "lucide-react";
import { Logo, LogoIcon } from "../components/Logo";

export default function Landing() {
  const features = [
    {
      icon: Target,
      title: "Define",
      description: "Build clear problem statements, SIPOC diagrams, and project charters with AI-guided refinement."
    },
    {
      icon: BarChart3,
      title: "Measure",
      description: "Upload data, generate control charts, and establish baselines with statistical rigor."
    },
    {
      icon: Search,
      title: "Analyze",
      description: "Create Pareto charts, fishbone diagrams, and identify root causes with AI assistance."
    },
    {
      icon: Lightbulb,
      title: "Improve",
      description: "Prioritize solutions, assess risks, and plan pilots with structured frameworks."
    },
    {
      icon: Shield,
      title: "Control",
      description: "Build control plans, set KPI alerts, and generate executive summaries."
    }
  ];

  const benefits = [
    "Structured DMAIC execution engine",
    "AI-powered problem refinement",
    "Statistical analysis automation",
    "Executive-ready reporting",
    "Financial impact tracking"
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoIcon className="w-9 h-9" />
            <span className="font-heading font-bold text-lg">BlackBelt AI</span>
            <span className="text-xs text-muted-foreground font-medium tracking-wider uppercase">Valdr</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/pricing">
              <Button variant="ghost" data-testid="pricing-nav-btn">Pricing</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" data-testid="login-nav-btn">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="register-nav-btn">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-sm font-medium text-primary uppercase tracking-wider">
                  AI Lean Six Sigma Black Belt
                </p>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                  Operational Excellence.{" "}
                  <span className="text-primary">Engineered.</span>
                </h1>
                <p className="text-lg text-muted-foreground max-w-lg">
                  A structured DMAIC execution platform that performs the functional role of 
                  a Lean Six Sigma Black Belt — guiding, analyzing, and driving improvement projects end-to-end.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto gap-2" data-testid="hero-get-started-btn">
                    Start Your Project
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto" data-testid="hero-signin-btn">
                    Sign In
                  </Button>
                </Link>
              </div>

              <div className="pt-4 space-y-3">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-card border border-border rounded-lg p-8 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Potential Savings</p>
                    <p className="text-2xl font-bold font-heading">$2.4M</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Projects</p>
                    <p className="text-xl font-bold font-heading">12</p>
                  </div>
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">ROI</p>
                    <p className="text-xl font-bold font-heading text-[hsl(var(--success))]">340%</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Project Progress</span>
                    <span className="font-medium">68%</span>
                  </div>
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <div className="h-full w-[68%] bg-primary rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DMAIC Features */}
      <section className="py-20 px-6 bg-card/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">
              DMAIC Methodology
            </p>
            <h2 className="font-heading text-3xl font-semibold">
              Structured Excellence at Every Phase
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Not a chatbot. A disciplined execution system that enforces DMAIC rigor 
              while providing intelligent guidance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors duration-200"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h2 className="font-heading text-3xl font-semibold">
              Master the Process
            </h2>
            <p className="text-muted-foreground">
              Start your first DMAIC project today and experience the precision of 
              AI-powered operational excellence.
            </p>
          </div>
          <Link to="/register">
            <Button size="lg" className="gap-2" data-testid="cta-get-started-btn">
              <FileText className="w-4 h-4" />
              Create Your First Project
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
              <Target className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-sm font-medium">BlackBelt AI - Valdr Edition</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Discipline Drives Performance.
          </p>
        </div>
      </footer>
    </div>
  );
}
